// Meridian's ground truth: the second world's deterministic heightfield,
// evaluated identically by the server (ship hover floor, spawn placement)
// and the client (terrain meshes) — the same wire-protocol-grade contract
// as the frontier's terrain.ts, at city proportions.
//
// The shape is a port capital rather than a wilderness: a dead-flat crown
// at the origin (the city stands on it, so building geometry needs no
// special cases — the settlement-plateau lesson), stepped terraces falling
// away on every side (a city is built ground; the steps read as made, not
// grown), and a low fen basin at the far edge of the map. No ridges, no
// mesas — Meridian's drama is architecture, not geology.
//
// Determinism law (terrain.ts states it in full): only IEEE-exact ops —
// + - * /, sqrt, floor, abs, min/max, integer ops. Math.sin/cos/pow/exp
// never appear here. The golden-value test locks this in.

import { fbm2, smoothstep, valueNoise2 } from "./terrain";

/** Meridian's edge length in metres (2^13 — a city world, not a frontier). */
export const CITY_SIZE = 8192;
/** Coordinates run −CITY_HALF..+CITY_HALF on x and z. */
export const CITY_HALF = CITY_SIZE / 2;

/** Inside this radius of the origin the city crown is dead flat. */
export const CITY_CROWN_RADIUS = 520;
/** By this radius the crown has fully blended into the terraces. */
export const CITY_CROWN_BLEND_RADIUS = 900;
/** The crown's elevation. Zero keeps every street/venue mesh trivial. */
export const CITY_CROWN_Y = 0;

/**
 * Meridian's single seed. Changing it is terraforming; treat as immutable
 * once characters walk the terraces. XOR-distinct from the frontier's
 * TERRAIN_SEED so no octave of one world can alias into the other.
 */
export const CITY_TERRAIN_SEED = 0x4d31a9;

// Derived sub-seeds; spacing arbitrary, stability load-bearing.
const SEED_WARP_X = CITY_TERRAIN_SEED + 101;
const SEED_WARP_Z = CITY_TERRAIN_SEED + 202;
const SEED_BASE = CITY_TERRAIN_SEED + 303;
const SEED_DETAIL = CITY_TERRAIN_SEED + 404;

/** Lateral distortion so terrace edges wander instead of reading gridded. */
const WARP_AMPLITUDE = 130;
const WARP_FREQ = 1 / 1100;
/** Terrace field base frequency (~1.6 km features — broad shelves). */
const BASE_FREQ = 1 / 1600;
/** Small surface undulation on each shelf. */
const DETAIL_FREQ = 1 / 240;

/** Height of one terrace riser, metres. */
export const CITY_TERRACE_STEP = 12;
/** How far the land has fallen by the outer basin, metres. */
const BASIN_DROP = 44;
/** Radial band over which the fall happens. */
const BASIN_NEAR = 900;
const BASIN_FAR = 3600;

/**
 * The terraced field before the crown mask: a rolling base quantized into
 * benches (mostly stepped, a little smooth blended back so risers are
 * climbable slopes rather than sheer walls), dropping radially toward the
 * basin so the crown reads as the high ground it fictionally is.
 */
function rawCityHeightAt(x: number, z: number): number {
  const wx = x + valueNoise2(SEED_WARP_X, x * WARP_FREQ, z * WARP_FREQ) * WARP_AMPLITUDE;
  const wz = z + valueNoise2(SEED_WARP_Z, x * WARP_FREQ, z * WARP_FREQ) * WARP_AMPLITUDE;

  const base = fbm2(SEED_BASE, wx * BASE_FREQ, wz * BASE_FREQ, 4); // [-1, 1]
  const t = base * 0.5 + 0.5;
  const stepped = Math.floor(t * 6) / 6;
  const shelves = (stepped * 0.78 + t * 0.22) * (CITY_TERRACE_STEP * 6) - CITY_TERRACE_STEP * 3;

  const r = Math.sqrt(x * x + z * z);
  const drop = smoothstep(BASIN_NEAR, BASIN_FAR, r) * BASIN_DROP;

  const detail = fbm2(SEED_DETAIL, wx * DETAIL_FREQ, wz * DETAIL_FREQ, 2) * 1.4;

  return shelves - drop + detail;
}

/**
 * Ground elevation anywhere on Meridian. Pure and total: callers may
 * evaluate outside ±CITY_HALF (chunk skirts do) and the field continues.
 */
export function cityHeightAt(x: number, z: number): number {
  const r = Math.sqrt(x * x + z * z);
  if (r >= CITY_CROWN_BLEND_RADIUS) return rawCityHeightAt(x, z);
  const mask = smoothstep(CITY_CROWN_RADIUS, CITY_CROWN_BLEND_RADIUS, r);
  if (mask === 0) return CITY_CROWN_Y;
  return CITY_CROWN_Y + (rawCityHeightAt(x, z) - CITY_CROWN_Y) * mask;
}

export const CITY_GROUNDS = ["crown", "terraces", "basin"] as const;
export type CityGround = (typeof CITY_GROUNDS)[number];

/**
 * Which band of the city a point stands in — crown (built city), terraces
 * (stepped skyline shelves), basin (the low fen at the map's edge). Used
 * for dressing and scatter, never for movement rules.
 */
export function cityGroundAt(x: number, z: number): CityGround {
  const rSq = x * x + z * z;
  if (rSq < CITY_CROWN_BLEND_RADIUS * CITY_CROWN_BLEND_RADIUS) return "crown";
  const mid = (BASIN_NEAR + BASIN_FAR) / 2;
  if (rSq < mid * mid) return "terraces";
  return "basin";
}
